import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../components/AuthContext";
import { tokenStorage } from "../utils/tokenStorage";

export default function LandingPage() {
  const navigate = useNavigate();
  const { userId, userRole } = useAuth();

  // Already logged in → skip landing
  useEffect(() => {
    if (userId && tokenStorage.isLoggedIn()) {
      if (userRole === "Admin") {
        navigate("/admin-dashboard", { replace: true });
      } else if (userRole) {
        navigate("/user-dashboard", { replace: true });
      }
    }
  }, [userId, userRole, navigate]);

  return (
    <div style={styles.page}>
      {/* ── Navbar ── */}
      <nav style={styles.nav}>
        <span style={styles.logo}>
          Gatii<span style={styles.logoAccent}>i</span>
        </span>
        <div style={styles.navActions}>
          <button style={styles.btnGhost} onClick={() => navigate("/login")}>
            Log in
          </button>
          <button style={styles.btnPrimary} onClick={() => navigate("/signup")}>
            Sign up free
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={styles.hero}>
        <div style={styles.badge}>
          <span style={styles.badgeDot} />
          Now available across India
        </div>

        <h1 style={styles.h1}>
          Your city,{" "}
          <em style={styles.h1Accent}>on your schedule</em>
        </h1>

        <p style={styles.heroSub}>
          Book rides, deliveries, and services — all from one place.
          Fast, reliable, and built for the way you move.
        </p>

        <div style={styles.heroCtas}>
          <button
            style={{ ...styles.btnHero, ...styles.btnHeroPrimary }}
            onClick={() => navigate("/signup")}
          >
            Get started free →
          </button>
          <button
            style={{ ...styles.btnHero, ...styles.btnHeroOutline }}
            onClick={() => navigate("/login")}
          >
            Log in to your account
          </button>
        </div>

        {/* Social proof */}
        <div style={styles.socialProof}>
          <div style={styles.avatars}>
            {[
              { initials: "RK", bg: "#E1F5EE", color: "#0F6E56" },
              { initials: "PA", bg: "#EEEDFE", color: "#534AB7" },
              { initials: "SM", bg: "#FAECE7", color: "#993C1D" },
              { initials: "NJ", bg: "#E6F1FB", color: "#185FA5" },
            ].map((av, i) => (
              <div
                key={av.initials}
                style={{
                  ...styles.avatar,
                  background: av.bg,
                  color: av.color,
                  marginLeft: i === 0 ? 0 : -8,
                }}
              >
                {av.initials}
              </div>
            ))}
          </div>
          <p style={styles.socialText}>
            Trusted by <strong>12,000+</strong> users in Delhi, Mumbai &amp; more
          </p>
        </div>
      </section>

      {/* ── Feature cards ── */}
      <div style={styles.sectionLabel}>Why Gatiii</div>

      <div style={styles.featureGrid}>
        {[
          {
            icon: "⚡",
            bg: "#E1F5EE",
            color: "#0F6E56",
            title: "Instant booking",
            desc: "Match with a driver or service provider in under 30 seconds.",
          },
          {
            icon: "🛡",
            bg: "#EEEDFE",
            color: "#534AB7",
            title: "Safe & verified",
            desc: "Every provider is background-checked and rated by real users.",
          },
          {
            icon: "⊞",
            bg: "#FAEEDA",
            color: "#854F0B",
            title: "All services, one app",
            desc: "Rides, food, errands, and more — no juggling between apps.",
          },
        ].map((f) => (
          <div key={f.title} style={styles.featureCard}>
            <div style={{ ...styles.featureIcon, background: f.bg, color: f.color }}>
              {f.icon}
            </div>
            <h3 style={styles.featureTitle}>{f.title}</h3>
            <p style={styles.featureDesc}>{f.desc}</p>
          </div>
        ))}
      </div>

      {/* ── Auth shortcuts ── */}
      <div style={styles.authShortcuts}>
        <div style={styles.authCard}>
          <p style={styles.authCardLabel}>Already have an account?</p>
          <button
            style={{ ...styles.btnHero, ...styles.btnHeroPrimary, width: "100%" }}
            onClick={() => navigate("/login")}
          >
            Log in
          </button>
        </div>
        <div style={styles.authCard}>
          <p style={styles.authCardLabel}>New to Gatiii?</p>
          <button
            style={{ ...styles.btnHero, ...styles.btnHeroOutline, width: "100%" }}
            onClick={() => navigate("/signup")}
          >
            Create a free account
          </button>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer style={styles.footer}>
        <span style={styles.logo}>
          Gatii<span style={styles.logoAccent}>i</span>
        </span>
        <div style={styles.footerLinks}>
          <span style={styles.footerLink} onClick={() => navigate("/about")}>
            About
          </span>
          <span style={styles.footerLink} onClick={() => navigate("/privacy-policy")}>
            Privacy
          </span>
          <span style={styles.footerLink} onClick={() => navigate("/terms-condition")}>
            Terms
          </span>
        </div>
        <p style={styles.footerCopy}>© {new Date().getFullYear()} Gatiii. All rights reserved.</p>
      </footer>
    </div>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────────── */
const TEAL = "#1D9E75";
const TEAL_DARK = "#0F6E56";

const styles = {
  page: {
    minHeight: "100vh",
    background: "#fff",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: "#1a1a1a",
  },

  /* Navbar */
  nav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 48px",
    borderBottom: "1px solid #f0f0f0",
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 10,
  },
  logo: { fontSize: 22, fontWeight: 600, letterSpacing: -0.5 },
  logoAccent: { color: TEAL },
  navActions: { display: "flex", gap: 10, alignItems: "center" },
  btnGhost: {
    padding: "8px 18px",
    borderRadius: 8,
    border: "1px solid #e0e0e0",
    background: "transparent",
    fontSize: 14,
    fontWeight: 500,
    color: "#1a1a1a",
    cursor: "pointer",
  },
  btnPrimary: {
    padding: "8px 20px",
    borderRadius: 8,
    border: "none",
    background: TEAL,
    fontSize: 14,
    fontWeight: 500,
    color: "#fff",
    cursor: "pointer",
  },

  /* Hero */
  hero: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "72px 24px 56px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    background: "#E1F5EE",
    color: "#0F6E56",
    fontSize: 12,
    fontWeight: 500,
    padding: "5px 14px",
    borderRadius: 100,
    marginBottom: 28,
  },
  badgeDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: TEAL,
    display: "inline-block",
  },
  h1: {
    fontSize: 50,
    fontWeight: 600,
    lineHeight: 1.15,
    letterSpacing: -2,
    maxWidth: 580,
    marginBottom: 18,
    fontStyle: "normal",
  },
  h1Accent: { color: TEAL, fontStyle: "normal" },
  heroSub: {
    fontSize: 17,
    color: "#555",
    maxWidth: 440,
    lineHeight: 1.7,
    marginBottom: 36,
  },
  heroCtas: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  btnHero: {
    padding: "12px 26px",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s",
  },
  btnHeroPrimary: { background: TEAL, color: "#fff", border: "none" },
  btnHeroOutline: {
    background: "transparent",
    color: "#1a1a1a",
    border: "1px solid #d0d0d0",
  },

  /* Social proof */
  socialProof: {
    marginTop: 48,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },
  avatars: { display: "flex" },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: "2px solid #fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
  },
  socialText: { fontSize: 13, color: "#777" },

  /* Section label */
  sectionLabel: {
    textAlign: "center",
    margin: "52px 0 28px",
    fontSize: 13,
    color: "#999",
    position: "relative",
  },

  /* Feature grid */
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 14,
    maxWidth: 740,
    margin: "0 auto",
    padding: "0 24px",
  },
  featureCard: {
    background: "#fff",
    border: "1px solid #ebebeb",
    borderRadius: 14,
    padding: 22,
  },
  featureIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    marginBottom: 14,
  },
  featureTitle: { fontSize: 14, fontWeight: 600, marginBottom: 6 },
  featureDesc: { fontSize: 13, color: "#666", lineHeight: 1.6 },

  /* Auth shortcuts */
  authShortcuts: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    maxWidth: 540,
    margin: "52px auto 0",
    padding: "0 24px 72px",
  },
  authCard: {
    background: "#fafafa",
    border: "1px solid #ebebeb",
    borderRadius: 14,
    padding: "22px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  authCardLabel: { fontSize: 13, color: "#888", margin: 0 },

  /* Footer */
  footer: {
    borderTop: "1px solid #f0f0f0",
    padding: "32px 48px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
  },
  footerLinks: { display: "flex", gap: 24 },
  footerLink: {
    fontSize: 13,
    color: "#888",
    cursor: "pointer",
    textDecoration: "underline",
    textDecorationColor: "transparent",
  },
  footerCopy: { fontSize: 12, color: "#bbb", margin: 0 },
};